import { useEffect, useState } from "react";

import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { motion, type Variants } from "framer-motion";
import {
  CheckCircle, Loader2, Store, Zap, BarChart3, Smartphone,
  ArrowRight, Star, Shield, Clock, Users, TrendingUp,
  QrCode, ChefHat, Truck, CreditCard, Bell, Palette
} from "lucide-react";
import { usePlatformConfig } from "@/hooks/usePlatformConfig";

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.1, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] as const }
  }),
};

const ParceiroLanding = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { config } = usePlatformConfig();
  const [loading, setLoading] = useState(true);
  const [affiliate, setAffiliate] = useState<any>(null);
  const [plans, setPlans] = useState<any[]>([]);

  useEffect(() => {
    if (!code) return;

    const track = async () => {
      const [affRes, plansRes] = await Promise.all([
        supabase
          .from("affiliates")
          .select("id, name, code, bio, avatar_url")
          .eq("code", code)
          .eq("is_active", true)
          .maybeSingle(),
        supabase
          .from("plans")
          .select("id, name, slug, price_monthly, description, features, is_featured")
          .eq("is_active", true)
          .order("sort_order"),
      ]);

      if (!affRes.data) { setLoading(false); return; }
      setAffiliate(affRes.data);
      setPlans(plansRes.data || []);

      // Record click (fire-and-forget)
      const params = new URLSearchParams(window.location.search);
      supabase.from("affiliate_clicks").insert({
        affiliate_id: affRes.data.id,
        landing_page: `/parceiro/${code}`,
        referrer_url: document.referrer || null,
        utm_source: params.get("utm_source"),
        utm_medium: params.get("utm_medium"),
        utm_campaign: params.get("utm_campaign"),
      });

      setLoading(false);
    };
    track();
  }, [code]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!affiliate) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold font-display mb-2">Link inválido</h1>
          <p className="text-muted-foreground">Este link de indicação não foi encontrado.</p>
          <Button className="mt-4" onClick={() => navigate("/")}>Ir para o site</Button>
        </div>
      </div>
    );
  }

  const ctaUrl = `/cadastro?ref=${affiliate.code}`;

  const features = [
    { icon: QrCode, title: "Cardápio Digital com QR Code", desc: "Seus clientes pedem direto da mesa, sem filas e sem erros." },
    { icon: Store, title: "PDV Completo", desc: "Ponto de venda rápido e intuitivo, integrado com cozinha." },
    { icon: ChefHat, title: "Painel da Cozinha", desc: "Pedidos em tempo real na tela da cozinha. Zero papel." },
    { icon: Truck, title: "Delivery Próprio", desc: "Gerencie entregas sem depender de marketplaces caros." },
    { icon: BarChart3, title: "Relatórios Inteligentes", desc: "Métricas de vendas, produtos mais vendidos e fluxo de caixa." },
    { icon: CreditCard, title: "Pagamentos Integrados", desc: "PIX, cartão e dinheiro — tudo registrado automaticamente." },
    { icon: Bell, title: "Notificações em Tempo Real", desc: "Acompanhe pedidos e atualizações instantaneamente." },
    { icon: Palette, title: "Personalização Total", desc: "Sua marca, suas cores, seu domínio personalizado." },
  ];

  const benefits = [
    "Sem taxa de adesão",
    "Período de teste grátis",
    "Suporte humano e dedicado",
    "Atualizações constantes e gratuitas",
    "Sem fidelidade — cancele quando quiser",
    "Funciona em qualquer dispositivo",
  ];

  const testimonials = [
    { name: "Carlos Silva", role: "Dono — Boteco do Carlos", text: "Em 2 semanas, reduzi 40% dos erros de pedido. O QR Code na mesa mudou completamente meu atendimento.", rating: 5 },
    { name: "Ana Beatriz", role: "Gerente — Pizzaria Napoli", text: "O delivery próprio nos economiza R$3.000/mês em taxas de marketplace. Fora a fidelização dos clientes.", rating: 5 },
    { name: "Roberto Mendes", role: "Chef — Bistrô Provence", text: "O painel da cozinha é genial. Os pedidos chegam em tempo real, com detalhes e prioridade. Zero confusão.", rating: 5 },
  ];

  const steps = [
    { num: "01", title: "Crie sua conta", desc: "Cadastro rápido em menos de 2 minutos" },
    { num: "02", title: "Monte seu cardápio", desc: "Adicione categorias, produtos e fotos" },
    { num: "03", title: "Compartilhe o QR Code", desc: "Imprima e coloque nas mesas" },
    { num: "04", title: "Receba pedidos!", desc: "Gerencie tudo em um só lugar" },
  ];

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      {/* ── Navbar ── */}
      <nav className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")} role="button">
            {config.logo_url ? (
              <img src={config.logo_url} alt={config.logo_text} className="h-8 rounded-xl object-contain" loading="eager" />
            ) : (
              <>
                <div className="w-8 h-8 rounded-xl bg-gradient-primary flex items-center justify-center">
                  <span className="text-primary-foreground font-bold text-xs">{config.logo_initials}</span>
                </div>
                <span className="font-display font-bold text-lg">{config.logo_text}</span>
              </>
            )}
          </div>
          <Button size="sm" onClick={() => navigate(ctaUrl)} className="bg-gradient-primary text-primary-foreground">
            Começar Grátis <ArrowRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--primary)/0.08),transparent_70%)]" />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          {affiliate.avatar_url && (
            <motion.img
              initial={{ scale: 0 }} animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
              src={affiliate.avatar_url}
              alt={affiliate.name}
              className="w-16 h-16 rounded-full mx-auto mb-4 border-2 border-primary/30 object-cover"
            />
          )}
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Users className="h-3.5 w-3.5 mr-1.5" />
            Indicação de {affiliate.name}
          </motion.div>

          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-bold font-display leading-tight mb-6">
            Transforme seu restaurante com
            <span className="text-transparent bg-clip-text bg-gradient-primary"> tecnologia</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Cardápio digital, pedidos por QR Code, delivery próprio, PDV, painel da cozinha, fidelidade e pagamentos — tudo em uma plataforma.
          </motion.p>

          {affiliate.bio && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
              className="text-sm text-muted-foreground italic max-w-lg mx-auto mb-6 bg-secondary/50 rounded-xl px-4 py-3">
              "{affiliate.bio}" — {affiliate.name}
            </motion.p>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button size="lg" onClick={() => navigate(ctaUrl)}
              className="bg-gradient-primary text-primary-foreground px-8 h-14 text-base font-semibold shadow-[var(--shadow-glow)]">
              Começar Grátis — Sem Cartão <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Shield className="h-3.5 w-3.5" /> Sem taxa de adesão • Teste grátis
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
            className="grid grid-cols-3 gap-4 max-w-md mx-auto mt-14">
            {[
              { value: "500+", label: "Restaurantes" },
              { value: "50k+", label: "Pedidos/mês" },
              { value: "4.9", label: "Avaliação" },
            ].map(s => (
              <div key={s.label}>
                <p className="text-2xl md:text-3xl font-bold font-display">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="text-center mb-14">
            <motion.h2 variants={fadeUp} custom={0}
              className="text-3xl md:text-4xl font-bold font-display mb-3">
              Tudo que seu restaurante precisa
            </motion.h2>
            <motion.p variants={fadeUp} custom={1}
              className="text-muted-foreground max-w-lg mx-auto">
              Uma plataforma completa para digitalizar, gerenciar e escalar seu negócio.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((f, i) => (
              <motion.div key={f.title} variants={fadeUp} custom={i}
                className="rounded-2xl border border-border bg-card p-6 hover:shadow-[var(--shadow-card)] transition-shadow">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <f.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-display font-semibold mb-1.5">{f.title}</h3>
                <p className="text-sm text-muted-foreground">{f.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="text-center mb-14">
            <motion.h2 variants={fadeUp} custom={0}
              className="text-3xl md:text-4xl font-bold font-display mb-3">
              Comece em 4 passos simples
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <motion.div key={s.num} variants={fadeUp} custom={i}
                className="text-center">
                <span className="text-4xl font-bold font-display text-primary/20">{s.num}</span>
                <h3 className="font-display font-semibold mt-2 mb-1">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="text-center mb-14">
            <motion.h2 variants={fadeUp} custom={0}
              className="text-3xl md:text-4xl font-bold font-display mb-3">
              Quem usa, recomenda
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-4">
            {testimonials.map((t, i) => (
              <motion.div key={t.name} variants={fadeUp} custom={i}
                className="rounded-2xl border border-border bg-card p-6">
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mb-4 italic">"{t.text}"</p>
                <div>
                  <p className="text-sm font-semibold">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.role}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Plans Preview ── */}
      {plans.length > 0 && (
        <section className="py-20">
          <div className="max-w-5xl mx-auto px-4">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}
              className="text-center mb-14">
              <motion.h2 variants={fadeUp} custom={0}
                className="text-3xl md:text-4xl font-bold font-display mb-3">
                Planos para todos os tamanhos
              </motion.h2>
              <motion.p variants={fadeUp} custom={1}
                className="text-muted-foreground">
                Comece grátis e evolua conforme seu restaurante crescer.
              </motion.p>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {plans.slice(0, 3).map((plan, i) => (
                <motion.div key={plan.id} variants={fadeUp} custom={i}
                  className={`rounded-2xl border p-6 ${plan.is_featured ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-border bg-card"}`}>
                  {plan.is_featured && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-medium bg-primary text-primary-foreground mb-3">
                      Mais Popular
                    </span>
                  )}
                  <h3 className="font-display font-bold text-lg">{plan.name}</h3>
                  <div className="mt-2 mb-4">
                    {plan.price_monthly === 0 ? (
                      <span className="text-3xl font-bold font-display">Grátis</span>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold font-display">
                          R$ {plan.price_monthly.toFixed(0).replace(".", ",")}
                        </span>
                        <span className="text-sm text-muted-foreground">/mês</span>
                      </div>
                    )}
                  </div>
                  {plan.description && (
                    <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
                  )}
                  <Button
                    onClick={() => navigate(`${ctaUrl}&plan=${plan.slug}`)}
                    className={`w-full ${plan.is_featured ? "bg-gradient-primary text-primary-foreground" : ""}`}
                    variant={plan.is_featured ? "default" : "outline"}
                  >
                    Começar Agora
                  </Button>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* ── Benefits Checklist ── */}
      <section className="py-20 bg-secondary/30">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp} custom={0}
            className="text-3xl md:text-4xl font-bold font-display mb-10">
            Por que escolher o ViaMenu?
          </motion.h2>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="grid sm:grid-cols-2 gap-3 text-left max-w-lg mx-auto">
            {benefits.map((b, i) => (
              <motion.div key={b} variants={fadeUp} custom={i}
                className="flex items-center gap-2.5 py-2">
                <CheckCircle className="h-5 w-5 text-primary shrink-0" />
                <span className="text-sm">{b}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,hsl(var(--primary)/0.08),transparent_70%)]" />
        <div className="max-w-3xl mx-auto px-4 text-center relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.h2 variants={fadeUp} custom={0}
              className="text-3xl md:text-5xl font-bold font-display mb-4">
              Pronto para transformar seu restaurante?
            </motion.h2>
            <motion.p variants={fadeUp} custom={1}
              className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
              Junte-se a centenas de restaurantes que já usam o ViaMenu para vender mais e gastar menos.
            </motion.p>
            <motion.div variants={fadeUp} custom={2}>
              <Button size="lg" onClick={() => navigate(ctaUrl)}
                className="bg-gradient-primary text-primary-foreground px-10 h-14 text-base font-semibold shadow-[var(--shadow-glow)]">
                Criar Minha Conta Grátis <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </motion.div>
            <motion.p variants={fadeUp} custom={3}
              className="text-xs text-muted-foreground mt-4 flex items-center justify-center gap-1">
              <Clock className="h-3.5 w-3.5" /> Cadastro em menos de 2 minutos
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-border py-8">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="font-display font-bold text-primary">ViaMenu</span>
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} ViaMenu. Todos os direitos reservados.
          </p>
          <div className="flex gap-4">
            <button onClick={() => navigate("/termos")} className="text-xs text-muted-foreground hover:text-foreground transition-colors">Termos</button>
            <button onClick={() => navigate("/privacidade")} className="text-xs text-muted-foreground hover:text-foreground transition-colors">Privacidade</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ParceiroLanding;
